import { CloseIcon, Drawer, InlineIconButton, PillButton, Typography, useBreakpoint } from "@povio/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

import { BellIcon } from "@/assets/icons/ui/Bell";
import NotificationItem from "@/components/notifications/NotificationItem";
import { Loader } from "@/components/ui/status/Loader/Loader";
import { RouteConfig } from "@/config/route.config";
import { NotificationsQueries } from "@/data/notifications/notifications.queries";
import { ProfileQueries } from "@/data/profile/profile.queries";
import useInvalidateProfile from "@/hooks/profile/useInvalidateProfile";
import { useToast } from "@/hooks/useToast";
import { DemoContext } from "@/providers/demo/DemoContext";
import { useNotificationStore } from "@/providers/notifications/notifications.store";
import { useClaimRewardModalStore } from "@/providers/wallet/wallet.store";

const isCashbackNotification = (type: string) => {
  return type === "CashbackAvailableNotification";
};

const isResumeExtractionFailedNotification = (type: string) => {
  return type === "ResumeExtractionFailedNotification";
};

const Notifications = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { setShowClaimRewardModal } = useClaimRewardModalStore();
  const [isOpen, setIsOpen] = useState(false);
  const { successToast, errorToast } = useToast();
  const { invalidateAndRefetchProfile } = useInvalidateProfile();
  const [page, setPage] = useState(1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isFirstLoadRef = useRef(true);
  const { isDemoMode } = DemoContext.useDemo();
  const isTablet = useBreakpoint("md");

  const { refetch: refetchReset } = ProfileQueries.useReset({ enabled: false });
  const { data: notificationsList, isLoading: isLoadingNotificationsList } = NotificationsQueries.useGetUser(
    {
      limit: 10,
      order: "-createdAt",
      page,
    },
    { enabled: !isDemoMode },
  );

  // Use store for notifications
  const {
    notifications,
    setNotifications,
    appendNotifications,
    updateNotification,
    deleteNotification,
    clearAllNotifications,
  } = useNotificationStore();

  const unreadCount = notifications.filter((notification) => !notification.readAt).length;

  const { mutate: markAsRead } = NotificationsQueries.useMarkAsRead({
    onSuccess: (_data, variables) => {
      const notificationId = variables?.id;
      if (!notificationId) return;

      const nowIso = new Date().toISOString();

      // Update store
      updateNotification(notificationId, { readAt: nowIso });

      queryClient.setQueryData(
        NotificationsQueries.keys.getUser(10, "-createdAt", undefined, undefined, undefined),
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: oldData.items?.map((n: any) => (n.id === notificationId ? { ...n, readAt: nowIso } : n)) ?? [],
          };
        },
      );

      queryClient.setQueryData(NotificationsQueries.keys.getUnread(), (oldUnread: any) => {
        if (!oldUnread) return oldUnread;
        return oldUnread.filter((n: any) => n.id !== notificationId);
      });
    },
  });

  const handleMarkAsRead = (notificationId: string) => {
    markAsRead({ id: notificationId });
  };

  const { mutate: deleteNotificationMutation } = NotificationsQueries.useDeleteNotification({
    onSuccess: (_data, variables) => {
      const notificationId = variables?.id;
      if (!notificationId) return;

      // Update store
      deleteNotification(notificationId);

      queryClient.setQueryData(
        NotificationsQueries.keys.getUser(10, "-createdAt", undefined, undefined, undefined),
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: oldData.items?.filter((n: any) => n.id !== notificationId) ?? [],
            totalItems: Math.max(0, (oldData.totalItems ?? 0) - 1),
          };
        },
      );

      queryClient.setQueryData(NotificationsQueries.keys.getUnread(), (oldUnread: any) => {
        if (!oldUnread) return oldUnread;
        return oldUnread.filter((n: any) => n.id !== notificationId);
      });
    },
  });

  const handleDelete = (notificationId: string) => {
    deleteNotificationMutation({ id: notificationId });
  };

  const loadMoreNotifications = useCallback(() => {
    if (!notificationsList || isLoadingNotificationsList) return;

    const { totalItems } = notificationsList;
    const currentItemsCount = notifications.length;

    // Only load more if we have more items available and haven't loaded all yet
    if (currentItemsCount < totalItems) {
      setPage((prev) => prev + 1);
    }
  }, [notificationsList, notifications.length, isLoadingNotificationsList]);

  const { mutate: deleteAll } = NotificationsQueries.useDeleteAll({
    onSuccess: () => {
      // Update store
      clearAllNotifications();

      queryClient.setQueryData(
        NotificationsQueries.keys.getUser(10, "-createdAt", undefined, undefined, undefined),
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: [],
            totalItems: 0,
          };
        },
      );

      successToast({
        text: "All notifications deleted",
      });
    },
  });

  // Sync React Query data to store
  useEffect(() => {
    if (!notificationsList?.items?.length) {
      return;
    }

    if (isFirstLoadRef.current) {
      setNotifications(notificationsList.items);
      isFirstLoadRef.current = false;
    } else {
      appendNotifications(notificationsList.items);
    }
  }, [notificationsList, setNotifications, appendNotifications]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

      if (isAtBottom) {
        loadMoreNotifications();
      }
    },
    [loadMoreNotifications],
  );

  return (
    <Drawer
      label="Notifications"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      trigger={
        <NotificationTriggerButton
          onPress={() => setIsOpen(true)}
          isDisabled={isDemoMode}
          unreadCount={unreadCount}
        />
      }
      overlayClassName="z-20"
      dialogClassName="h-full"
      className="w-full dm:max-w-full t:max-w-[380px] bg-elevation-fill-default-2"
      isDismissable={true}
    >
      {(close) => (
        <>
          <div className="flex flex-row items-center justify-end py-2 pr-4 pl-5">
            <InlineIconButton
              size="m"
              variant="text"
              color="secondary"
              label="Close"
              icon={CloseIcon}
              onPress={close}
              className="border-0 p-button-height-m"
              disableTooltip
            />
          </div>
          <div className="flex h-[calc(100%-72px)] flex-col gap-6 p-6 pt-0">
            <div className="flex flex-row items-center justify-between">
              <Typography
                size={isTablet ? "title-5" : "title-3"}
                variant="prominent-1"
              >
                Notifications
              </Typography>
              <PillButton
                variant="outlined"
                color="secondary"
                onPress={() => deleteAll({})}
                isDisabled={!notifications?.length}
              >
                Delete All
              </PillButton>
            </div>
            <div
              ref={scrollContainerRef}
              className="flex flex-1 flex-col gap-0-5 overflow-y-auto"
              onScroll={handleScroll}
            >
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  markNotificationAsRead={() => handleMarkAsRead(notification.id)}
                  onDelete={() => handleDelete(notification.id)}
                  onNotificationClicked={async () => {
                    if (isCashbackNotification(notification.type)) {
                      router.push(RouteConfig.MyWallet);
                      setShowClaimRewardModal(true);
                    } else if (isResumeExtractionFailedNotification(notification.type)) {
                      // Handle "Try again" action - reset profile and redirect to onboarding
                      try {
                        const res = await refetchReset();
                        if (res.status === "error") {
                          errorToast({
                            text: "There was an error resetting your profile, try again later.",
                          });
                        } else if (res.status === "success") {
                          await invalidateAndRefetchProfile();
                          router.replace(RouteConfig.Onboarding);
                        }
                      } catch (error) {
                        console.error(error);
                        errorToast({
                          text: "There was an error resetting your profile, try again later.",
                        });
                      }
                    }
                    setIsOpen(false);
                  }}
                  clickable={
                    isCashbackNotification(notification.type) || isResumeExtractionFailedNotification(notification.type)
                  }
                />
              ))}
              {!notifications?.length && !isLoadingNotificationsList && (
                <Typography
                  size={isTablet ? "body-4" : "body-2"}
                  variant="default-italic"
                  className="m-auto w-full text-center text-text-default-3"
                >
                  No notifications
                </Typography>
              )}
              {isLoadingNotificationsList && (
                <div className="flex justify-center py-2">
                  <div className="text-neutral-400">
                    <Loader size="l" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default Notifications;

const NotificationTriggerButton = ({
  onPress,
  isDisabled,
  unreadCount,
}: {
  onPress: () => void;
  isDisabled: boolean;
  unreadCount: number;
}) => {
  return (
    <div className="relative flex h-9 w-9 items-center justify-center">
      <InlineIconButton
        icon={BellIcon}
        size="xs"
        color="secondary"
        disableTooltip
        onPress={onPress}
        isDisabled={isDisabled}
        label="Notifications"
        className="h-9 w-9 p-height-xs!"
      />
      {unreadCount > 0 && (
        <div className="pointer-events-none absolute bottom-2 left-2 box-border h-2-5 w-2-5 rounded-full border-[1.5px] border-interactive-contained-secondary-idle bg-text-error-1" />
      )}
    </div>
  );
};
